package dev.lingvo.site.pages.auth

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.AcceptedInvitationDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import dev.lingvo.site.i18n.t

internal const val PENDING_INVITATION_KEY = "lingvo:pendingInvite"

/** Only this explicit first-party continuation is supported; parked tokens do not imply intent. */
internal fun invitationAuthDestination(next: String?): String = if (next == "/invite") "/invite" else "/app"

/** The pinned router can reinterpret encoded query fragments; read the browser query once. */
internal fun invitationAuthDestinationFromSearch(search: String): String {
    val parameters = js("new URLSearchParams(search)")
    val values = parameters.getAll("next")
    return if ((values.length as Int) == 1) invitationAuthDestination(values[0] as String) else "/app"
}

internal fun invitationAuthFormPath(path: String, destination: String): String {
    require(path == "/login" || path == "/signup")
    return if (destination == "/invite") "$path?next=%2Finvite" else path
}

private data class InvitationOwner(
    val userId: String,
    val credential: String,
    val route: String,
)

/** A committed invitation and the later, read-only verification of its workspace membership. */
@Stable
internal class InvitationHandoff(
    private val invitationToken: String,
    private val scope: CoroutineScope,
) {
    private var owner: InvitationOwner? = null
    private var generation = 0L
    private var receipt by mutableStateOf<AcceptedInvitationDto?>(null)
    private var membershipVerified by mutableStateOf(false)
    private var acceptingState by mutableStateOf(false)
    private var refreshingState by mutableStateOf(false)
    private var acceptanceError by mutableStateOf<String?>(null)
    private var readError by mutableStateOf<String?>(null)

    val accepted: AcceptedInvitationDto?
        get() = receipt?.takeIf { owns() }
    val accepting: Boolean
        get() = acceptingState && owns()
    val refreshing: Boolean
        get() = refreshingState && owns()
    val apiError: String?
        get() = acceptanceError?.takeIf { owns() }
    val currentMembership: WorkspaceDto?
        get() {
            val joined = accepted ?: return null
            if (!membershipVerified || Session.state !is SessionState.Authenticated) return null
            val matches = Session.organizations.filter { it.id == joined.organization.id || it.slug == joined.organization.slug }
            return matches.singleOrNull()?.takeIf {
                it.id == joined.organization.id && it.slug == joined.organization.slug
            }
        }
    val canOpen: Boolean
        get() = !refreshing && currentMembership != null
    val setupProblem: String?
        get() = if (accepted != null && !refreshing && !canOpen) readError ?: workspaceReadProblem() else null

    fun accept() {
        if (invitationToken.isBlank() || acceptingState || receipt != null) return
        val current = Session.state as? SessionState.Authenticated ?: return
        val credential = Session.token?.takeIf(String::isNotBlank) ?: return
        val captured = InvitationOwner(current.user.id, credential, currentInvitationRoute())
        if (!owns(captured)) return
        owner = captured
        val request = ++generation
        acceptingState = true
        acceptanceError = null
        scope.launch {
            try {
                val response = ApiClient.acceptInvitation(invitationToken)
                if (!owns(captured, request)) return@launch
                receipt = response
                // Only remove the token this acknowledged request actually owned.
                if (localStorage.getItem(PENDING_INVITATION_KEY) == invitationToken) {
                    localStorage.removeItem(PENDING_INVITATION_KEY)
                }
                acceptingState = false
                refreshMembership(captured, request)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                if (owns(captured, request)) {
                    if (receipt != null) readError = workspaceReadProblem()
                    else acceptanceError = if (failure is ApiException) failure.message else tr(
                        "auth.invitation_handoff.couldnt_reach_lingvo_check_your_connection",
                        "Couldn't reach Lingvo. Check your connection and retry.",
                    )
                }
            } finally {
                if (owns(captured, request)) acceptingState = false
            }
        }
    }

    fun retryWorkspaceSetup() {
        val captured = owner ?: return
        if (accepted == null || refreshingState || !owns(captured)) return
        val request = ++generation
        scope.launch { refreshMembership(captured, request) }
    }

    fun openWorkspace(onOpen: () -> Unit) {
        if (!canOpen || !owns()) return
        val membership = currentMembership ?: return
        WorkspaceSelection.select(membership.slug)
        onOpen()
    }

    private suspend fun refreshMembership(captured: InvitationOwner, request: Long) {
        if (!owns(captured, request) || receipt == null) return
        refreshingState = true
        membershipVerified = false
        readError = null
        try {
            val response = Session.loadMe()
            if (!owns(captured, request)) return
            val joined = receipt ?: return
            val returned = response?.organizations?.filter { it.id == joined.organization.id || it.slug == joined.organization.slug }
                ?.singleOrNull()
            val published = (Session.state as? SessionState.Authenticated)?.organizations
                ?.filter { it.id == joined.organization.id || it.slug == joined.organization.slug }?.singleOrNull()
            if (returned == null || response?.user?.id != captured.userId || Session.user?.id != captured.userId ||
                returned.id != joined.organization.id || returned.slug != joined.organization.slug ||
                published != returned
            ) {
                readError = workspaceReadProblem()
                return
            }
            membershipVerified = true
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            if (owns(captured, request)) readError = workspaceReadProblem()
        } finally {
            if (owns(captured, request)) refreshingState = false
        }
    }

    private fun owns(): Boolean = owner?.let { owns(it) } == true

    private fun owns(captured: InvitationOwner, request: Long = generation): Boolean {
        if (request != generation || currentInvitationRoute() != captured.route || Session.token != captured.credential) return false
        val pending = localStorage.getItem(PENDING_INVITATION_KEY)
        if (pending != null && pending != invitationToken) return false
        return when (val current = Session.state) {
            is SessionState.Authenticated -> current.user.id == captured.userId
            // Same-credential read failure/loading retains the committed receipt, never readiness.
            is SessionState.Failure, SessionState.Loading -> true
            SessionState.LoggedOut -> false
        }
    }
}

private fun currentInvitationRoute(): String = window.location.pathname + window.location.search + window.location.hash

private fun workspaceReadProblem(): String = tr(
    "auth.invitation_handoff.your_invitation_was_accepted_but_current",
    "Your invitation was accepted, but current workspace access could not be confirmed. Retry the setup without accepting again.",
)
